// src/components/CustomerProgressVisuals.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase'; // Assuming the path to your Firebase config
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';

/**
 * Custom Tooltip for Recharts to show weight and date clearly
 */
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip" style={{ 
        backgroundColor: '#fff', 
        border: '1px solid #ccc', 
        padding: '10px', 
        borderRadius: '5px' 
      }}>
        <p className="label" style={{ fontWeight: 'bold' }}>{`Date: ${label}`}</p>
        <p className="intro" style={{ color: payload[0].stroke }}>{`Weight: ${payload[0].value} kg`}</p>
      </div>
    );
  }
  return null;
};


/**
 * Functional component to render the responsive Line Chart
 */
const WeightTrendChart = ({ data }) => {
  if (data.length === 0) {
    return <p>No daily weight data available for charting. Ensure the customer is logging their daily weight.</p>;
  }

  // Find min/max weight for dynamic Y-axis domain
  const weights = data.map(d => d.weight);
  const minWeight = Math.floor(Math.min(...weights) * 0.95);
  const maxWeight = Math.ceil(Math.max(...weights) * 1.05);

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart 
        data={data}
        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
        <XAxis dataKey="date" stroke="#354f6b" />
        <YAxis 
          label={{ value: 'Weight (kg)', angle: -90, position: 'insideLeft', fill: '#354f6b' }}
          domain={[minWeight, maxWeight]}
          stroke="#354f6b"
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend verticalAlign="top" height={36}/>
        <Line 
          type="monotone" 
          dataKey="weight" 
          stroke="#4CAF50" // Primary green color
          strokeWidth={3}
          dot={false}
          name="Weight Trend"
          activeDot={{ r: 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};


/**
 * Main component to fetch data and render visuals
 */
const CustomerProgressVisuals = ({ customerUid }) => {
  const [loading, setLoading] = useState(true);
  const [initialWeight, setInitialWeight] = useState(null);
  const [currentWeight, setCurrentWeight] = useState(null);
  const [dailyWeightData, setDailyWeightData] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!customerUid) {
      setInitialWeight(null);
      setCurrentWeight(null);
      setDailyWeightData([]);
      setLoading(false);
      return;
    }

    const fetchProgressData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // 1. Fetch the main progress document (initial and current weight)
        const progressDocRef = doc(db, 'progress', customerUid);
        const progressDocSnap = await getDoc(progressDocRef);

        if (progressDocSnap.exists()) {
          const data = progressDocSnap.data();
          setInitialWeight(data.initialWeight);
          setCurrentWeight(data.currentWeight);
        } else {
          setInitialWeight(null);
          setCurrentWeight(null);
        }

        // 2. Fetch the dailyProgress sub-collection (for the trend chart)
        const dailyProgressCollectionRef = collection(db, 'progress', customerUid, 'dailyProgress');
        const dailyProgressSnapshot = await getDocs(dailyProgressCollectionRef);

        const fetchedDailyData = dailyProgressSnapshot.docs
          .map(docSnap => {
            const dateStr = docSnap.id; 
            const data = docSnap.data();
            
            // Assuming the daily document contains the 'weight' logged for that day. 
            // Change 'weight' field name below if your data structure uses something different (e.g., 'dailyWeight').
            const weight = parseFloat(data.currentWeight);
            
            return {
              // Format date for better X-axis readability (e.g., '04/07')
              date: new Date(dateStr).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' }), 
              weight: isNaN(weight) ? null : parseFloat(weight.toFixed(1)),
            };
          })
          .filter(item => item.weight !== null)
          .sort((a, b) => new Date(a.date) - new Date(b.date)); // Sort by date

        setDailyWeightData(fetchedDailyData);

      } catch (err) {
        console.error("Error fetching customer progress:", err);
        setError("Failed to load progress data.");
      } finally {
        setLoading(false);
      }
    };

    fetchProgressData();
  }, [customerUid]);

  if (loading) {
    return <div className="progress-visuals-container"><p>Loading progress visuals...</p></div>;
  }

  if (error) {
    return <div className="progress-visuals-container"><p className="upload-error">{error}</p></div>;
  }

  const weightLoss = initialWeight && currentWeight ? (initialWeight - currentWeight).toFixed(1) : 'N/A';
  
  // Calculate total percentage progress towards a goal (assuming initial weight is the "start")
  // Note: You may want to fetch a specific weight goal from the customer's profile for a better calculation
  const progressPercent = initialWeight && currentWeight && initialWeight !== 0 ? (100 - (currentWeight / initialWeight * 100)).toFixed(1) : 0;
  
  return (
    <div className="progress-visuals-container">
      <h3>Customer Progress Overview 📈</h3>
      
      <div className="key-metrics-cards">
        <div className="metric-card">
          <strong>Initial Weight:</strong> {initialWeight ? `${initialWeight} kg` : 'N/A'}
        </div>
        <div className="metric-card">
          <strong>Current Weight:</strong> {currentWeight ? `${currentWeight} kg` : 'N/A'}
        </div>
        <div className="metric-card" style={{color: weightLoss > 0 ? '#4CAF50' : (weightLoss < 0 ? '#e53935' : '#354f6b')}}>
          <strong>Total Loss:</strong> {weightLoss !== 'N/A' ? `${Math.abs(weightLoss)} kg ${weightLoss >= 0 ? '⬇️' : '⬆️'}` : 'N/A'}
        </div>
        <div className="metric-card">
          <strong>Total Progress (%):</strong> {progressPercent}%
        </div>
      </div>
      
      <div className="weight-trend-chart">
        <WeightTrendChart data={dailyWeightData} />
      </div>

    </div>
  );
};

export default CustomerProgressVisuals;